/**
 * Kasa Integration Store
 *
 * Zustand store for TP-Link Kasa/Tapo smart device state.
 * Manages devices, settings, and Socket.IO real-time updates.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { api, getValidatedOrganizationContextHeaderValue } from '@/services/api/client';
import { workflowApi } from '@/services/api/workflowApi';
import { getCurrentWorkflowUserId } from '@/utils/workflow-utils';
import * as errorUtils from '@/utils/errors';

// ============================================================================
// Types
// ============================================================================

export type KasaDeviceType = 'plug' | 'switch' | 'dimmer' | 'bulb' | 'strip' | 'unknown';

export interface KasaDevice {
  deviceId: string;
  host: string;
  alias: string;
  model: string;
  deviceType: KasaDeviceType;
  protocol: 'xor' | 'klap';
  status: 'on' | 'off' | 'unreachable';
  brightness?: number;
  childId?: string;
  customAlias?: string;
  description?: string;
  supportsEmeter: boolean;
  supportsBrightness: boolean;
  lastSeen: string;
  controllerDeviceId?: string;  // Pi/ESP32 that controls this device
  _orgSlug?: string;            // org this device was fetched from (for control routing)
  _source?: 'direct' | 'esp32';
  _discoveredBy?: string;
}

export interface KasaEnergyReading {
  deviceId: string;
  current_ma: number;
  voltage_mv: number;
  power_mw: number;
  total_wh: number;
  timestamp: string;
}

interface KasaSettingsResponseData {
  hasCredentials?: boolean;
  isActive?: boolean;
  devices?: KasaDevice[];
}

interface ESP32KasaDeviceResponse {
  deviceId: string;
  name?: string;
  type: string;
  status?: string;
  lastSeen?: string;
  discoveredBy?: string;
  metadata?: {
    description?: string;
  };
  integrationMeta?: {
    host?: string;
    alias?: string;
    model?: string;
    protocol?: 'xor' | 'klap';
    relayState?: 'on' | 'off';
    brightness?: number;
    supportsEmeter?: boolean;
    supportsBrightness?: boolean;
  };
  integration?: string;
}

interface ESP32DevicesEnvelope {
  success?: boolean;
  data?: {
    devices?: ESP32KasaDeviceResponse[];
    counts?: Record<string, number>;
  };
  devices?: ESP32KasaDeviceResponse[];
}

interface KasaState {
  // Settings
  hasCredentials: boolean;
  isActive: boolean;

  // Data
  devices: KasaDevice[];
  esp32Devices: KasaDevice[];
  energy: Record<string, KasaEnergyReading>;

  // UI state
  isLoading: boolean;
  isDiscovering: boolean;
  isControlling: boolean;
  error: string | null;

  // Actions
  fetchSettings: () => Promise<void>;
  saveCredentials: (email: string, password: string) => Promise<void>;
  removeCredentials: () => Promise<void>;
  discoverDevices: () => Promise<void>;
  fetchDevices: () => Promise<void>;
  controlDevice: (deviceId: string, action: 'on' | 'off' | 'toggle') => Promise<void>;
  setBrightness: (deviceId: string, level: number) => Promise<void>;
  fetchEnergy: (deviceId: string) => Promise<void>;
  startPolling: () => Promise<void>;
  handleDeviceStatusUpdate: (data: { devices: KasaDevice[] }) => void;
  clearError: () => void;

  // Device metadata
  renameDevice: (deviceId: string, customAlias: string) => Promise<void>;
  updateDeviceDescription: (deviceId: string, description: string) => Promise<void>;

  // Transfer
  transferDevice: (deviceId: string, target: { orgSlug: string } | { personal: true }) => Promise<void>;

  // ESP32-discovered device actions
  fetchESP32Devices: () => Promise<void>;
  controlESP32Device: (deviceId: string, action: 'on' | 'off' | 'toggle') => Promise<void>;
  setESP32Brightness: (deviceId: string, level: number) => Promise<void>;
  renameESP32Device: (deviceId: string, name: string) => Promise<void>;
  updateESP32DeviceDescription: (deviceId: string, description: string) => Promise<void>;
}

const API_BASE = '/integrations/kasa';

// ============================================================================
// Owner Context Helpers
// ============================================================================

/**
 * Returns "?orgSlug=<slug>" when an org context is active, otherwise "".
 * Appended to all direct Kasa API calls so the backend can scope the
 * request to the correct org or personal namespace.
 */
async function getOwnerQueryParam(): Promise<string> {
  const orgSlug = await getValidatedOrganizationContextHeaderValue();
  return orgSlug ? `?orgSlug=${encodeURIComponent(orgSlug)}` : '';
}

function mapDeviceType(type: string): KasaDeviceType {
  const map: Record<string, KasaDeviceType> = {
    kasa_plug: 'plug', kasa_switch: 'switch', kasa_dimmer: 'dimmer',
    kasa_bulb: 'bulb', kasa_strip: 'strip',
  };
  return map[type] || 'unknown';
}

/**
 * Build the workflow backend device API route based on current org/user context.
 * Returns e.g. "/api/orgs/my-org/devices" or "/api/users/abc123/devices"
 */
async function buildDeviceApiRoute(): Promise<string | null> {
  const orgSlug = await getValidatedOrganizationContextHeaderValue();
  if (orgSlug) {
    return `/api/orgs/${orgSlug}/devices`;
  }
  const userId = getCurrentWorkflowUserId();
  return userId ? `/api/users/${userId}/devices` : null;
}

// ============================================================================
// Store
// ============================================================================

export const useKasaStore = create<KasaState>()(
  subscribeWithSelector(
    immer((set) => ({
      hasCredentials: false,
      isActive: false,
      devices: [],
      esp32Devices: [],
      energy: {},
      isLoading: false,
      isDiscovering: false,
      isControlling: false,
      error: null,

      fetchSettings: async () => {
        try {
          const q = await getOwnerQueryParam();
          const response = await api.get<KasaSettingsResponseData>(`${API_BASE}/settings${q}`);
          const data = response.data;
          set((state) => {
            state.hasCredentials = data?.hasCredentials ?? false;
            state.isActive = data?.isActive ?? false;
          });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to fetch settings';
          });
        }
      },

      saveCredentials: async (email: string, password: string) => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const q = await getOwnerQueryParam();
          const response = await api.put<KasaSettingsResponseData>(`${API_BASE}/settings${q}`, { email, password });
          const data = response.data;
          set((state) => {
            state.hasCredentials = true;
            state.devices = data?.devices ?? [];
            state.isLoading = false;
          });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to save credentials';
            state.isLoading = false;
          });
        }
      },

      removeCredentials: async () => {
        try {
          const q = await getOwnerQueryParam();
          await api.delete(`${API_BASE}/settings${q}`);
          set((state) => {
            state.hasCredentials = false;
            state.isActive = false;
            state.devices = [];
            state.energy = {};
          });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to remove credentials';
          });
        }
      },

      discoverDevices: async () => {
        set((state) => { state.isDiscovering = true; state.error = null; });
        try {
          const q = await getOwnerQueryParam();
          const response = await api.post<KasaDevice[]>(`${API_BASE}/discover${q}`);
          set((state) => {
            state.devices = response.data ?? [];
            state.isDiscovering = false;
          });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Discovery failed';
            state.isDiscovering = false;
          });
        }
      },

      fetchDevices: async () => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          // Fetch devices for current org context only
          // AI agents use a separate path (getDevicesAcrossUserOrgs) for cross-org access
          const q = await getOwnerQueryParam();
          const orgSlug = q ? new URLSearchParams(q.substring(1)).get('orgSlug') : null;
          const response = await api.get<KasaDevice[]>(`${API_BASE}/devices${q}`);
          const devices = (response.data ?? []).map((d) => ({
            ...d,
            _orgSlug: orgSlug || undefined,
          }));

          set((state) => { state.devices = devices; state.isLoading = false; });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to fetch devices';
            state.isLoading = false;
          });
        }
      },

      controlDevice: async (deviceId: string, action: 'on' | 'off' | 'toggle') => {
        console.log('[KasaStore] controlDevice called:', deviceId, action);
        set((state) => { state.isControlling = true; });
        try {
          // Find the device to get its org scope
          const device = useKasaStore.getState().devices.find((d) => d.deviceId === deviceId || d.host === deviceId);
          const orgSlug = device?._orgSlug;
          const q = orgSlug ? `?orgSlug=${encodeURIComponent(orgSlug)}` : await getOwnerQueryParam();
          console.log('[KasaStore] sending:', `${API_BASE}/devices/${encodeURIComponent(deviceId)}/${action}${q}`);
          await api.post(`${API_BASE}/devices/${encodeURIComponent(deviceId)}/${action}${q}`);
          set((state) => {
            const device = state.devices.find((d) => d.deviceId === deviceId || d.host === deviceId);
            if (device) {
              if (action === 'on') device.status = 'on';
              else if (action === 'off') device.status = 'off';
              else if (action === 'toggle') device.status = device.status === 'on' ? 'off' : 'on';
            }
            state.isControlling = false;
          });
        } catch (error) {
          console.error('[KasaStore] controlDevice error:', error);
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Control failed';
            state.isControlling = false;
          });
        }
      },

      setBrightness: async (deviceId: string, level: number) => {
        set((state) => { state.isControlling = true; });
        try {
          const dev = useKasaStore.getState().devices.find((d) => d.deviceId === deviceId || d.host === deviceId);
          const bq = dev?._orgSlug ? `?orgSlug=${encodeURIComponent(dev._orgSlug)}` : await getOwnerQueryParam();
          await api.post(`${API_BASE}/devices/${encodeURIComponent(deviceId)}/brightness${bq}`, { level });
          set((state) => {
            const device = state.devices.find((d) => d.deviceId === deviceId || d.host === deviceId);
            if (device) device.brightness = level;
            state.isControlling = false;
          });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Brightness control failed';
            state.isControlling = false;
          });
        }
      },

      fetchEnergy: async (deviceId: string) => {
        try {
          const q = await getOwnerQueryParam();
          const response = await api.get<KasaEnergyReading>(`${API_BASE}/devices/${encodeURIComponent(deviceId)}/energy${q}`);
          const data = response.data;
          if (data) {
            set((state) => {
              state.energy[deviceId] = data;
            });
          }
        } catch {
          // Energy fetch is optional, don't set error
        }
      },

      startPolling: async () => {
        try {
          const q = await getOwnerQueryParam();
          await api.post(`${API_BASE}/polling/start${q}`, { intervalMs: 10000 });
        } catch {
          // Polling start failure is non-fatal
        }
      },

      handleDeviceStatusUpdate: (data) => {
        set((state) => {
          state.devices = data.devices;
        });
      },

      clearError: () => {
        set((state) => { state.error = null; });
      },

      // ==================================================================
      // Device Metadata
      // ==================================================================

      renameDevice: async (deviceId: string, customAlias: string) => {
        try {
          const rd = useKasaStore.getState().devices.find((d) => d.deviceId === deviceId);
          const rq = rd?._orgSlug ? `?orgSlug=${encodeURIComponent(rd._orgSlug)}` : await getOwnerQueryParam();
          await api.put(`${API_BASE}/devices/${encodeURIComponent(deviceId)}${rq}`, { customAlias });
          set((state) => {
            const device = state.devices.find((d) => d.deviceId === deviceId);
            if (device) device.customAlias = customAlias;
          });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to rename device';
          });
        }
      },

      updateDeviceDescription: async (deviceId: string, description: string) => {
        try {
          const dd = useKasaStore.getState().devices.find((d) => d.deviceId === deviceId);
          const dq = dd?._orgSlug ? `?orgSlug=${encodeURIComponent(dd._orgSlug)}` : await getOwnerQueryParam();
          await api.put(`${API_BASE}/devices/${encodeURIComponent(deviceId)}${dq}`, { description });
          set((state) => {
            const device = state.devices.find((d) => d.deviceId === deviceId);
            if (device) device.description = description;
          });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to update description';
          });
        }
      },

      // ==================================================================
      // Transfer
      // ==================================================================

      transferDevice: async (deviceId, target) => {
        set((state) => { state.isControlling = true; });
        try {
          const q = await getOwnerQueryParam();
          const body = 'orgSlug' in target
            ? { targetOrgSlug: target.orgSlug }
            : { targetType: 'personal' };
          await api.post(`${API_BASE}/devices/${encodeURIComponent(deviceId)}/transfer${q}`, body);
          set((state) => {
            state.devices = state.devices.filter(
              (d) => d.deviceId !== deviceId && d.host !== deviceId
            );
            state.isControlling = false;
          });
        } catch (error: unknown) {
          const msg = errorUtils.getErrorMessageOrFallback(error, 'Transfer failed');
          set((state) => {
            state.error = msg;
            state.isControlling = false;
          });
          throw error;
        }
      },

      // ==================================================================
      // ESP32-Discovered Device Actions
      // ==================================================================

      fetchESP32Devices: async () => {
        try {
          const url = await buildDeviceApiRoute();
          if (!url) return;
          const result = await workflowApi.get<ESP32DevicesEnvelope>(url);
          // Handle the sendSuccessResponse envelope: { success, data: { devices, counts } }
          const allDevices = result.data?.devices ?? result.devices ?? [];
          const mapped: KasaDevice[] = allDevices
            .filter((d) => d.integration === 'kasa')
            .map((d) => ({
              deviceId: d.deviceId,
              host: d.integrationMeta?.host || '',
              alias: d.name || d.integrationMeta?.alias || d.deviceId,
              model: d.integrationMeta?.model || 'Unknown',
              deviceType: mapDeviceType(d.type),
              protocol: d.integrationMeta?.protocol || 'xor',
              status: d.status !== 'online' ? 'unreachable'
                : d.integrationMeta?.relayState === 'on' ? 'on' : 'off',
              brightness: d.integrationMeta?.brightness,
              supportsEmeter: d.integrationMeta?.supportsEmeter || false,
              supportsBrightness: d.integrationMeta?.supportsBrightness || false,
              lastSeen: d.lastSeen || new Date().toISOString(),
              description: d.metadata?.description,
              _source: 'esp32' as const,
              _discoveredBy: d.discoveredBy,
            }));
          set((state) => { state.esp32Devices = mapped; });
        } catch (error: unknown) {
          const msg = errorUtils.getErrorMessageOrFallback(
            error,
            'Failed to fetch ESP32 devices',
          );
          set((state) => { state.error = msg; });
        }
      },

      controlESP32Device: async (deviceId: string, action: 'on' | 'off' | 'toggle') => {
        set((state) => { state.isControlling = true; });
        try {
          const baseUrl = await buildDeviceApiRoute();
          if (!baseUrl) throw new Error('No device API route');
          await workflowApi.post(`${baseUrl}/${encodeURIComponent(deviceId)}/kasa/${action}`);
          set((state) => {
            const device = state.esp32Devices.find((d) => d.deviceId === deviceId);
            if (device) {
              if (action === 'on') device.status = 'on';
              else if (action === 'off') device.status = 'off';
              else if (action === 'toggle') device.status = device.status === 'on' ? 'off' : 'on';
            }
            state.isControlling = false;
          });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'ESP32 device control failed';
            state.isControlling = false;
          });
        }
      },

      setESP32Brightness: async (deviceId: string, level: number) => {
        set((state) => { state.isControlling = true; });
        try {
          const baseUrl = await buildDeviceApiRoute();
          if (!baseUrl) throw new Error('No device API route');
          await workflowApi.post(`${baseUrl}/${encodeURIComponent(deviceId)}/kasa/brightness`, { brightness: level });
          set((state) => {
            const device = state.esp32Devices.find((d) => d.deviceId === deviceId);
            if (device) device.brightness = level;
            state.isControlling = false;
          });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'ESP32 brightness control failed';
            state.isControlling = false;
          });
        }
      },

      renameESP32Device: async (deviceId: string, name: string) => {
        try {
          const baseUrl = await buildDeviceApiRoute();
          if (!baseUrl) throw new Error('No device API route');
          await workflowApi.put(`${baseUrl}/${encodeURIComponent(deviceId)}`, { name });
          set((state) => {
            const device = state.esp32Devices.find((d) => d.deviceId === deviceId);
            if (device) device.alias = name;
          });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to rename device';
          });
        }
      },

      updateESP32DeviceDescription: async (deviceId: string, description: string) => {
        try {
          const baseUrl = await buildDeviceApiRoute();
          if (!baseUrl) throw new Error('No device API route');
          await workflowApi.put(`${baseUrl}/${encodeURIComponent(deviceId)}`, { metadata: { description } });
          set((state) => {
            const device = state.esp32Devices.find((d) => d.deviceId === deviceId);
            if (device) device.description = description;
          });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to update description';
          });
        }
      },
    }))
  )
);
